/**
 * AccessSubTab.tsx
 *
 * The patient's Access Labs orders, newest first, mirroring VibrantSubTab's
 * table. Each row says "Sent", or "Not sent" with the reason in plain words
 * (labs_access_orders.deliveryError, written staff-safe by the server). A
 * not-sent order saved in full (canResend) has a Resend button, which sends
 * the same order again with nothing re-entered (KTD15). A resend that fails
 * leaves the row "Not sent" with the new reason. A recent order the server
 * marks `sending` may still reach Access, so it shows "Sending" and no Resend.
 * An `unconfirmed` order may already be at Access: it shows "Not confirmed",
 * and Send again asks staff to check with Access before it sends.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Spinner } from '@/components/ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { getPatientAccessOrders, resendAccessOrder, type AccessOrder } from '@/services/labs.service';
import { getErrorCode } from '@/services/apiClient';
import { accessOrdersQueryKey } from '@/lib/accessLabs';
import { accessOrderErrorMessage } from '@/lib/accessLabsErrors';

const RESEND_FAILED = "The order couldn't be resent just now. Try again, and contact support if it keeps happening.";
const LOAD_FAILED = "Access Labs orders couldn't be loaded just now. Press Refresh to try again, and contact support if it keeps happening.";
const NOT_SENT_CAN_RESEND = "This order didn't reach Access Labs. Press Resend to send it again.";
const NOT_SENT_CANNOT_RESEND = "This order didn't reach Access Labs. Place the order again to send it.";
const SENDING = 'This order is on its way to Access Labs. Refresh the page in a few minutes to see whether it went.';
const NOT_CONFIRMED = "Access Labs may have received this order, but didn't confirm it. Check with Access Labs before sending it again.";

/** Server codes whose reason the server has already stored on the order row. */
const STORED_ON_ROW = new Set(['ORDER_NOT_SENT', 'ORDER_NOT_CONFIRMED']);

/**
 * A server date for display. Order and sent times are stored in UTC
 * ('YYYY-MM-DD HH:MM:SS'); a collection date is the clinic's wall clock,
 * either a bare date or 'YYYY-MM-DDTHH:MM', and a bare date is read as a local
 * day (new Date('YYYY-MM-DD') would be UTC midnight, the day before here).
 */
function formatDate(value: string | null | undefined, utc: boolean): string {
  if (!value) return '—';
  const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = bare
    ? new Date(Number(bare[1]), Number(bare[2]) - 1, Number(bare[3]))
    : new Date(utc ? `${value.replace(' ', 'T')}Z` : value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface AccessSubTabProps {
  patientId: number;
}

export function AccessSubTab({ patientId }: AccessSubTabProps) {
  const queryClient = useQueryClient();
  const queryKey = accessOrdersQueryKey(patientId);
  // The reason a resend just failed with, per order, until the next resend.
  const [resendReasons, setResendReasons] = useState<Record<number, string>>({});
  // The unconfirmed order whose Send again is waiting for staff to confirm.
  const [confirmOrderId, setConfirmOrderId] = useState<number | null>(null);

  const { data: orders, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => getPatientAccessOrders(patientId),
    staleTime: 60_000,
  });

  const resend = useMutation({
    mutationFn: ({ orderId, confirmNotReceived }: { orderId: number; confirmNotReceived: boolean }) =>
      resendAccessOrder(orderId, confirmNotReceived),
    onMutate: ({ orderId }) => {
      setResendReasons((prev) => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    },
    onSuccess: (_result, { orderId }) => {
      queryClient.setQueryData<AccessOrder[]>(queryKey, (prev) =>
        prev?.map((o) => (o.orderId === orderId ? { ...o, deliveryStatus: 'sent', deliveryError: null, canResend: false, unconfirmed: false } : o)),
      );
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err, { orderId }) => {
      // Not sent again, or not confirmed: the server has stored the new
      // reason on the order, in the words the row already shows.
      if (!STORED_ON_ROW.has(getErrorCode(err) ?? '')) {
        setResendReasons((prev) => ({ ...prev, [orderId]: accessOrderErrorMessage(err, RESEND_FAILED) }));
      }
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;

  if (isError) {
    return <p className="py-4 text-sm text-red-600">{LOAD_FAILED}</p>;
  }

  if (!orders || orders.length === 0) {
    return <p className="py-4 text-sm text-slate-400 italic">No Access Labs orders yet.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3 w-[15%] whitespace-nowrap">Order Date</th>
            <th className="px-4 py-3 w-[15%] whitespace-nowrap">Collect Date</th>
            <th className="px-4 py-3">Test</th>
            <th className="px-4 py-3 w-[35%]">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {orders.map((o) => {
            const sent = o.deliveryStatus === 'sent';
            const resending = resend.isPending && resend.variables?.orderId === o.orderId;
            const reason = resendReasons[o.orderId]
              ?? o.deliveryError
              ?? (o.unconfirmed ? NOT_CONFIRMED : o.canResend ? NOT_SENT_CAN_RESEND : NOT_SENT_CANNOT_RESEND);
            return (
              <tr key={o.orderId} className="align-top">
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(o.createDate, true)}</td>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(o.collectionDate, false)}</td>
                <td className="px-4 py-3 text-slate-800 [overflow-wrap:anywhere] min-w-0">{o.testsSummary ?? '—'}</td>
                <td className="px-4 py-3">
                  {sent ? (
                    <Badge variant="success" size="sm">Sent</Badge>
                  ) : o.sending ? (
                    <div className="space-y-1">
                      <Badge variant="info" size="sm">Sending</Badge>
                      <p className="text-xs text-slate-600">{SENDING}</p>
                    </div>
                  ) : o.unconfirmed ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="warning" size="sm">Not confirmed</Badge>
                        {o.canResend && (
                          <Button size="sm" variant="secondary" disabled={resending} onClick={() => setConfirmOrderId(o.orderId)}>
                            {resending ? <Spinner className="h-3.5 w-3.5" /> : 'Send again'}
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">{reason}</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="error" size="sm">Not sent</Badge>
                        {o.canResend && (
                          <Button size="sm" variant="secondary" disabled={resending} onClick={() => resend.mutate({ orderId: o.orderId, confirmNotReceived: false })}>
                            {resending ? <Spinner className="h-3.5 w-3.5" /> : 'Resend'}
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">{reason}</p>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <ConfirmDialog
        isOpen={confirmOrderId !== null}
        onClose={() => setConfirmOrderId(null)}
        onConfirm={() => {
          if (confirmOrderId !== null) resend.mutate({ orderId: confirmOrderId, confirmNotReceived: true });
        }}
        title="Send this order again?"
        message="Access Labs may already have this order. Send it again only if Access Labs told you they didn't receive it. Otherwise they will receive it twice."
        confirmLabel="Send again"
        variant="warning"
      />
    </div>
  );
}
